<template>
  <div class="block-featured-faqs container">
    <featured-block-header :title="section.title" :intro="section.intro" />
    <div class="block-featured__inner" :class="{ 'has-menu': hasMenu }">
      <categories-menu v-if="hasMenu">
        <template #reset>
          <button v-if="activeCategory" class="ais-ClearRefinements-button" type="button" @click="clearCategory">Clear</button>
        </template>

        <div class="ais-HierarchicalMenu">
          <ul class="ais-HierarchicalMenu-list ais-HierarchicalMenu-list--lvl0">
            <li
              v-for="category in categoryTree"
              :key="category.value"
              class="ais-HierarchicalMenu-item"
              :class="{
                'ais-HierarchicalMenu-item--selected': isCategoryBranchActive(category.value),
                'ais-HierarchicalMenu-item--parent': category.children.length > 0,
              }"
            >
              <a
                href=""
                class="ais-HierarchicalMenu-link"
                :class="{ 'ais-HierarchicalMenu-link--selected': isCategoryActive(category.value) }"
                @click.prevent="toggleCategory(category.value)"
              >
                <span class="ais-HierarchicalMenu-label">{{ category.title }}</span>
              </a>

              <ul v-if="shouldShowChildren(category)" class="ais-HierarchicalMenu-list ais-HierarchicalMenu-list--child">
                <li
                  v-for="child in category.children"
                  :key="child.value"
                  class="ais-HierarchicalMenu-item"
                  :class="{ 'ais-HierarchicalMenu-item--selected': isCategoryActive(child.value) }"
                >
                  <a
                    href=""
                    class="ais-HierarchicalMenu-link"
                    :class="{ 'ais-HierarchicalMenu-link--selected': isCategoryActive(child.value) }"
                    @click.prevent="toggleCategory(child.value)"
                  >
                    <span class="ais-HierarchicalMenu-label">{{ child.title }}</span>
                  </a>
                </li>
              </ul>
            </li>
          </ul>
        </div>
      </categories-menu>

      <div>
        <featured-faqs-list :items="paginatedItems" />

        <div class="block-featured-faqs__pagination">
          <featured-pagination
            :show-label="false"
            :create-url="createUrl"
            :refine="refinePage"
            :nb-pages="nbPages"
            :nb-hits="filteredItems.length"
            :current-refinement="currentPage"
            :is-first-page="isFirstPage"
            :is-last-page="isLastPage"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { ensureArrayValue } from '@/utils/blocks';
import FeaturedBlockHeader from '~/components/global/components/featured/FeaturedBlockHeader.vue';
import CategoriesMenu from '~/components/global/components/categories-menu/CategoriesMenu.vue';
import FeaturedFaqsList from '~/components/global/components/featured/FeaturedFaqsList.vue';
import FeaturedPagination from '~/components/global/components/featured/FeaturedPagination.vue';
import type { SectionProps } from '~/types/components';
import type { LooseObject } from '~/types/generic';
import { useSectionProps } from '~/composables/blocks';

interface FaqCategoryNode {
  title: string;
  value: string;
  children: Array<{
    title: string;
    value: string;
  }>;
}

const props: SectionProps = defineProps(useSectionProps());
const activeCategory = ref<string | null>(null);
const currentPage = ref(0);

const faqItems = computed(() =>
  ensureArrayValue(props.section?.faq_items)
    .map((item, index) => normalizeFaqItem(item, index))
    .filter((item) => item.title && (item.description || item.answer))
);

const categoryTree = computed<FaqCategoryNode[]>(() => buildCategoryTree(faqItems.value));
const hasMenu = computed(() => !props.section?.hide_categories_filter && categoryTree.value.length > 0);
const hitsPerPage = computed(() => {
  const configuredValue = Number(props.section?.items_per_page);

  if (!props.section?.unlimited_items_per_page && Number.isFinite(configuredValue) && configuredValue > 0) {
    return configuredValue;
  }

  return faqItems.value.length || 1;
});

const filteredItems = computed(() => {
  if (!activeCategory.value) {
    return faqItems.value;
  }

  return faqItems.value.filter((item) => matchesCategory(item, activeCategory.value));
});

const nbPages = computed(() => Math.max(1, Math.ceil(filteredItems.value.length / hitsPerPage.value)));
const paginatedItems = computed(() => {
  const start = currentPage.value * hitsPerPage.value;

  return filteredItems.value.slice(start, start + hitsPerPage.value);
});
const isFirstPage = computed(() => currentPage.value <= 0);
const isLastPage = computed(() => currentPage.value >= nbPages.value - 1);

watch([activeCategory, hitsPerPage, () => filteredItems.value.length], () => {
  currentPage.value = 0;
});

function toggleCategory(value: string) {
  activeCategory.value = activeCategory.value === value ? null : value;
}

function clearCategory() {
  activeCategory.value = null;
}

function refinePage(page: number) {
  currentPage.value = Math.min(Math.max(page, 0), nbPages.value - 1);
}

function createUrl() {
  return '#';
}

function isCategoryActive(value: string) {
  return activeCategory.value === value;
}

function isCategoryBranchActive(value: string) {
  return activeCategory.value === value || activeCategory.value?.startsWith(`${value} > `);
}

function shouldShowChildren(category: FaqCategoryNode) {
  return category.children.length > 0 && isCategoryBranchActive(category.value);
}

function normalizeFaqItem(item: LooseObject, index: number): LooseObject {
  const title = typeof item?.title === 'string' && item.title.trim() ? item.title.trim() : item?.question?.trim?.() || '';
  const description = item?.description;

  return {
    ...item,
    id: typeof item?.id === 'string' && item.id ? item.id : `faq-${index}`,
    title,
    description: hasRichDescription(description) ? description : null,
    categories: normalizeCategories(item?.categories),
  };
}

function hasRichDescription(value: unknown) {
  if (!value || typeof value !== 'object') {
    return false;
  }

  if (Array.isArray(value)) {
    return value.length > 0;
  }

  return Object.keys(value).length > 0;
}

function normalizeCategories(categories: LooseObject | null | undefined): LooseObject | null {
  if (!categories || typeof categories !== 'object') {
    return null;
  }

  return Object.fromEntries(
    Object.entries(categories).map(([level, values]) => [level, ensureArrayValue(values).filter((value) => typeof value === 'string')])
  );
}

function matchesCategory(item: LooseObject, categoryValue: string) {
  const categories = item.categories || {};
  const lvl0 = ensureArrayValue(categories.lvl0).filter((value) => typeof value === 'string');
  const lvl1 = ensureArrayValue(categories.lvl1).filter((value) => typeof value === 'string');

  if (categoryValue.includes(' > ')) {
    return lvl1.includes(categoryValue);
  }

  return lvl0.includes(categoryValue) || lvl1.some((value) => value.startsWith(`${categoryValue} > `));
}

function buildCategoryTree(items: LooseObject[]): FaqCategoryNode[] {
  const categoriesMap = new Map<string, FaqCategoryNode>();

  items.forEach((item) => {
    const categories = item.categories || {};
    const lvl0 = ensureArrayValue(categories.lvl0).filter((value) => typeof value === 'string');
    const lvl1 = ensureArrayValue(categories.lvl1).filter((value) => typeof value === 'string');

    lvl0.forEach((value) => {
      if (!categoriesMap.has(value)) {
        categoriesMap.set(value, {
          title: value,
          value,
          children: [],
        });
      }
    });

    lvl1.forEach((value) => {
      const parts = value
        .split('>')
        .map((part) => part.trim())
        .filter(Boolean);
      const parent = parts[0];
      const title = parts[parts.length - 1];

      if (!parent || !title) {
        return;
      }

      if (!categoriesMap.has(parent)) {
        categoriesMap.set(parent, {
          title: parent,
          value: parent,
          children: [],
        });
      }

      const category = categoriesMap.get(parent);

      if (category && !category.children.some((child) => child.value === value)) {
        category.children.push({
          title,
          value,
        });
      }
    });
  });

  return Array.from(categoriesMap.values());
}
</script>
